import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { PLATFORMS } from '../components/common/PlatformIcon';
import { aiApi } from '../lib/api';
import {
  Wand2, Image, Type, Mic, Video, Sparkles, Loader2, CheckCircle2,
  XCircle, Download, Copy, ArrowRight, Zap
} from 'lucide-react';
import { toast } from 'sonner';

function GenerationResult({ label, icon: Icon, result, error, loading, backendUrl, onCopy }) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 animate-pulse">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Generating {label}...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
        <XCircle className="w-5 h-5 text-destructive shrink-0" />
        <div>
          <p className="text-sm font-medium text-destructive">{label} Failed</p>
          <p className="text-xs text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (!result) return null;

  return (
    <Card className="border-emerald-200 dark:border-emerald-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-semibold">{label}</span>
          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">Generated</Badge>
        </div>

        {result.caption && (
          <div className="bg-muted/50 rounded-lg p-3 mb-2">
            <p className="text-sm whitespace-pre-wrap">{result.caption}</p>
            {onCopy && (
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => onCopy(result.caption)}>
                <Copy className="w-3 h-3 mr-1" /> Copy Caption
              </Button>
            )}
          </div>
        )}

        {result.url && result.url.endsWith('.png') && (
          <div className="rounded-lg overflow-hidden bg-muted mt-2 max-w-sm">
            <img src={`${backendUrl}${result.url}`} alt="Generated" className="w-full h-auto" />
          </div>
        )}

        {result.url && result.url.endsWith('.mp3') && (
          <div className="mt-2">
            <audio controls className="w-full" src={`${backendUrl}${result.url}`} />
          </div>
        )}

        {result.url && result.url.endsWith('.mp4') && (
          <div className="rounded-lg overflow-hidden bg-muted mt-2 max-w-sm">
            <video controls className="w-full" src={`${backendUrl}${result.url}`} />
          </div>
        )}

        {result.media_asset && (
          <p className="text-xs text-muted-foreground mt-2">Saved to media library</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function CompanyAI() {
  const { companyId } = useOutletContext();
  const queryClient = useQueryClient();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Individual generation states
  const [captionPrompt, setCaptionPrompt] = useState('');
  const [captionPlatform, setCaptionPlatform] = useState('');
  const [captionResult, setCaptionResult] = useState(null);

  const [imagePrompt, setImagePrompt] = useState('');
  const [imageResult, setImageResult] = useState(null);

  const [voiceText, setVoiceText] = useState('');
  const [voiceLang, setVoiceLang] = useState('auto');
  const [voiceResult, setVoiceResult] = useState(null);

  // Pipeline states
  const [pipelinePrompt, setPipelinePrompt] = useState('');
  const [pipelinePlatform, setPipelinePlatform] = useState('');
  const [pipelineGenImage, setPipelineGenImage] = useState(true);
  const [pipelineGenVoice, setPipelineGenVoice] = useState(true);
  const [pipelineGenVideo, setPipelineGenVideo] = useState(true);
  const [pipelineResult, setPipelineResult] = useState(null);

  const captionMutation = useMutation({
    mutationFn: (data) => aiApi.generateCaption(companyId, data),
    onSuccess: (res) => {
      setCaptionResult(res.data);
      toast.success('Caption generated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Caption generation failed'),
  });

  const imageMutation = useMutation({
    mutationFn: (data) => aiApi.generateImage(companyId, data),
    onSuccess: (res) => {
      setImageResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['media', companyId] });
      toast.success('Image generated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Image generation failed'),
  });

  const voiceMutation = useMutation({
    mutationFn: (data) => aiApi.generateVoice(companyId, data),
    onSuccess: (res) => {
      setVoiceResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['media', companyId] });
      toast.success('Voice generated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Voice generation failed'),
  });

  const pipelineMutation = useMutation({
    mutationFn: (data) => aiApi.runPipeline(companyId, data),
    onSuccess: (res) => {
      setPipelineResult(res.data);
      queryClient.invalidateQueries({ queryKey: ['media', companyId] });
      toast.success('Content pipeline complete!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Pipeline failed'),
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h2 className="text-xl font-semibold font-display flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" /> AI Content Studio
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Generate captions, images, voice, and videos with AI</p>
      </div>

      <Tabs defaultValue="pipeline" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="pipeline" className="gap-1.5"><Zap className="w-3.5 h-3.5" /> Full Pipeline</TabsTrigger>
          <TabsTrigger value="caption" className="gap-1.5"><Type className="w-3.5 h-3.5" /> Caption</TabsTrigger>
          <TabsTrigger value="image" className="gap-1.5"><Image className="w-3.5 h-3.5" /> Image</TabsTrigger>
          <TabsTrigger value="voice" className="gap-1.5"><Mic className="w-3.5 h-3.5" /> Voice</TabsTrigger>
        </TabsList>

        {/* ===== FULL PIPELINE ===== */}
        <TabsContent value="pipeline" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" /> Full Content Pipeline
              </CardTitle>
              <CardDescription>
                Enter a text idea and generate caption + image + voice + video automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Content Idea / Prompt *</Label>
                <Textarea
                  value={pipelinePrompt}
                  onChange={(e) => setPipelinePrompt(e.target.value)}
                  placeholder="E.g. Summer sale promotion for our new collection, 30% off all items..."
                  rows={3}
                  data-testid="pipeline-prompt"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target Platform</Label>
                  <Select value={pipelinePlatform} onValueChange={setPipelinePlatform}>
                    <SelectTrigger><SelectValue placeholder="All platforms" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">All Platforms</SelectItem>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3 pt-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Generate Image</Label>
                    <Switch checked={pipelineGenImage} onCheckedChange={setPipelineGenImage} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Generate Voice</Label>
                    <Switch checked={pipelineGenVoice} onCheckedChange={setPipelineGenVoice} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Generate Video</Label>
                    <Switch checked={pipelineGenVideo} onCheckedChange={setPipelineGenVideo} />
                  </div>
                </div>
              </div>
              <Button
                onClick={() => pipelineMutation.mutate({
                  prompt: pipelinePrompt,
                  platform: pipelinePlatform || '',
                  language: 'auto',
                  generate_image: pipelineGenImage,
                  generate_voice: pipelineGenVoice,
                  generate_video: pipelineGenVideo,
                })}
                disabled={!pipelinePrompt.trim() || pipelineMutation.isPending}
                className="w-full sm:w-auto"
                data-testid="pipeline-generate-button"
              >
                {pipelineMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating content...</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" /> Generate All Content</>
                )}
              </Button>

              {pipelineMutation.isPending && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <div>
                      <p className="text-sm font-medium">Generating content...</p>
                      <p className="text-xs text-muted-foreground">This may take 30-60 seconds. Image generation takes the longest.</p>
                    </div>
                  </div>
                </div>
              )}

              {pipelineResult && (
                <div className="space-y-3 pt-2">
                  <Separator />
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Generated Content
                    <Badge variant="outline" className="text-xs">{pipelineResult.steps?.length || 0} steps completed</Badge>
                  </h4>

                  {pipelineResult.caption && (
                    <GenerationResult
                      label="Caption"
                      icon={Type}
                      result={pipelineResult.caption}
                      backendUrl={backendUrl}
                      onCopy={copyToClipboard}
                    />
                  )}
                  {pipelineResult.image && (
                    <GenerationResult label="Image" icon={Image} result={pipelineResult.image} backendUrl={backendUrl} />
                  )}
                  {pipelineResult.voice && (
                    <GenerationResult label="Voice" icon={Mic} result={pipelineResult.voice} backendUrl={backendUrl} />
                  )}
                  {pipelineResult.video && (
                    <GenerationResult label="Video" icon={Video} result={pipelineResult.video} backendUrl={backendUrl} />
                  )}

                  {pipelineResult.errors?.length > 0 && (
                    <div className="space-y-2">
                      {pipelineResult.errors.map((err, i) => (
                        <GenerationResult key={i} label={err.step} icon={XCircle} error={err.error} />
                      ))}
                    </div>
                  )}

                  {pipelineResult.media_ids?.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {pipelineResult.media_ids.length} assets saved to media library
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CAPTION TAB ===== */}
        <TabsContent value="caption" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Caption Generator</CardTitle>
              <CardDescription>Generate social media captions from a text prompt</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Prompt *</Label>
                <Textarea
                  value={captionPrompt}
                  onChange={(e) => setCaptionPrompt(e.target.value)}
                  placeholder="Describe what the post is about..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={captionPlatform} onValueChange={setCaptionPlatform}>
                  <SelectTrigger><SelectValue placeholder="General" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    {PLATFORMS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => captionMutation.mutate({ prompt: captionPrompt, platform: captionPlatform, language: 'auto' })}
                disabled={!captionPrompt.trim() || captionMutation.isPending}
              >
                {captionMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Type className="w-4 h-4 mr-2" /> Generate Caption</>}
              </Button>
              {captionResult && (
                <GenerationResult label="Caption" icon={Type} result={captionResult} backendUrl={backendUrl} onCopy={copyToClipboard} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== IMAGE TAB ===== */}
        <TabsContent value="image" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Image Generator</CardTitle>
              <CardDescription>Generate social media visuals, banners, and graphics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Image Prompt *</Label>
                <Textarea
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="E.g. Modern minimalist promotional banner for summer sale with pastel colors..."
                  rows={3}
                />
              </div>
              <Button
                onClick={() => imageMutation.mutate({ prompt: imagePrompt, save_to_library: true })}
                disabled={!imagePrompt.trim() || imageMutation.isPending}
              >
                {imageMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating (up to 60s)...</> : <><Image className="w-4 h-4 mr-2" /> Generate Image</>}
              </Button>
              {imageResult && (
                <GenerationResult label="Image" icon={Image} result={imageResult} backendUrl={backendUrl} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== VOICE TAB ===== */}
        <TabsContent value="voice" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">Voice Generator</CardTitle>
              <CardDescription>Generate voice narration from text</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Text to speak *</Label>
                <Textarea
                  value={voiceText}
                  onChange={(e) => setVoiceText(e.target.value)}
                  placeholder="Enter the text you want to convert to speech..."
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={voiceLang} onValueChange={setVoiceLang}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="tr">Turkish</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => voiceMutation.mutate({ text: voiceText, language: voiceLang, save_to_library: true })}
                disabled={!voiceText.trim() || voiceMutation.isPending}
              >
                {voiceMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Mic className="w-4 h-4 mr-2" /> Generate Voice</>}
              </Button>
              {voiceResult && (
                <GenerationResult label="Voice" icon={Mic} result={voiceResult} backendUrl={backendUrl} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
